import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  IconButton,
  DialogContent,
  Typography,
  DialogActions,
  Button,
  Box,
  Alert,
} from "@mui/material";
import Close from "@mui/icons-material/Close";
import Park from "@mui/icons-material/Park";
import WarningAmber from "@mui/icons-material/WarningAmber";
import NumberField from "../../../../../../../../Component/NumberField/NumberField";
import { useTranslation } from "react-i18next";
import { CalibrationItem } from "../Calibration.types";
import { MIN_GT_FOR_MAPE } from "../utils/yield-error";
import { UncertaintyImprovementPreview } from "./UncertaintyImprovementPreview";

function getBackgroundColor(color: string): string {
  const fillString = `var(${color})`;
  return `color-mix(in srgb, ${fillString} 20%, transparent)`;
}

export function CalibrationDialog({
  open,
  onClose,
  selectedPlot,
  onSave,
  currentSampleCount,
  isNewSample,
  isEdgePlot,
}: {
  open: boolean;
  onClose: () => void;
  selectedPlot: CalibrationItem;
  onSave: ({
    selectedPlot,
    manualCount,
  }: {
    selectedPlot: CalibrationItem;
    manualCount: number;
  }) => void;
  currentSampleCount: number;
  isNewSample: boolean;
  isEdgePlot: boolean;
}) {
  const [manualCount, setManualCount] = useState<number | null>(null);
  const [error, setError] = useState<string>("");
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (selectedPlot && selectedPlot.manualCount !== undefined) {
      setManualCount(selectedPlot.manualCount);
    } else {
      setManualCount(null);
    }
  }, [selectedPlot]);

  const saveManualCount = ({
    selectedPlot,
    manualCount,
  }: {
    selectedPlot: CalibrationItem;
    manualCount: number | null;
  }) => {
    if (manualCount === null) {
      setError(
        `${t("text.calibrate.observed_apple_count")} ${t("text.error.is_required")}.`,
      );
      return;
    }

    if (manualCount > 10000 || manualCount < 0) {
      setError(
        `${t("text.calibrate.observed_apple_count")} ${t(
          "text.validation.range",
          {
            min: 0,
            max: new Intl.NumberFormat(i18n.language).format(10000),
          },
        )}.`,
      );
      return;
    }

    try {
      onSave({ selectedPlot, manualCount });
      setError("");
    } catch (error) {
      setError(error instanceof Error ? error.message : String(error));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 3,
            boxShadow: 24,
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 2,
          borderBottom: 1,
          borderColor: "divider",
          fontSize: "1rem",
          fontWeight: 700,
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          {t("header.calibrate.dialog.title")}
        </Typography>

        <IconButton onClick={onClose} size="small" sx={{ mr: -1 }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: "24px !important" }}>
        <>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mb: 3,
              p: 2,
              bgcolor: "background.default",
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor:
                  selectedPlot.yieldCategory !== 2
                    ? getBackgroundColor(selectedPlot.color)
                    : "color-mix(in srgb, var(--color-apple-calibration-map-2-alternative) 20%, transparent)",
                color:
                  selectedPlot.yieldCategory !== 2
                    ? `var(${selectedPlot.color})`
                    : "var(--color-apple-calibration-map-2-alternative)",
              }}
            >
              <Park />
            </Box>
            <Box>
              <Typography
                variant="caption"
                fontWeight="bold"
                color="text.secondary"
                sx={{ textTransform: "uppercase", letterSpacing: 1 }}
              >
                {t("text.calibrate.tree")} {selectedPlot.plotId}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {t(
                  `label.calibrate.yield.category-${selectedPlot.yieldCategory}`,
                )}
              </Typography>
            </Box>
          </Box>
          {isEdgePlot && (
            <Alert severity="warning" icon={<WarningAmber />} sx={{ mb: 2 }}>
              {t("text.calibrate.edge_tree_warning")}
            </Alert>
          )}
          <NumberField
            label={t("text.calibrate.observed_apple_count")}
            size="small"
            value={manualCount}
            onValueChange={setManualCount}
            locale={i18n.language}
            error={!!error}
            required
          />
          {error && <Alert severity="error">{error}</Alert>}
          {isNewSample && currentSampleCount >= MIN_GT_FOR_MAPE && (
            <UncertaintyImprovementPreview
              currentSampleCount={currentSampleCount}
            />
          )}
        </>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          pt: 1,
          borderTop: 1,
          borderColor: "divider",
          bgcolor: "background.default",
        }}
      >
        <Button onClick={onClose} color="inherit" sx={{ fontWeight: "bold" }}>
          {t("button.Cancel")}
        </Button>

        <Button
          onClick={() => saveManualCount({ selectedPlot, manualCount })}
          variant="contained"
          sx={{ fontWeight: "bold", px: 3 }}
        >
          {t("button.Save changes")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
